<!-- PedidoPaso1.vue — Upload PDF + campos del pedido -->
<template>
  <div>
    <!-- ── Upload PDF (compacto) ─────────────────────────── -->
    <div
      class="upload-zone mb-4"
      :class="{ 'has-file': pdfFile }"
      @click="$refs.inputPdf.click()"
      @dragover.prevent
      @drop.prevent="handleDrop"
    >
      <input
        ref="inputPdf"
        type="file"
        accept="application/pdf"
        class="d-none"
        @change="handleChange"
      />
      <template v-if="!pdfFile">
        <div class="upload-inner">
          <i class="bi bi-cloud-arrow-up upload-icon"></i>
          <div>
            <p class="fw-bold text-cafe mb-0">Arrastra PDF</p>
            <small class="text-muted">o haz clic (opcional)</small>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="upload-inner">
          <i class="bi bi-file-earmark-check upload-icon text-verde-oscuro"></i>
          <div>
            <p class="fw-bold text-cafe mb-0">{{ pdfFile.name }}</p>
            <small class="text-verde fw-bold"
              >PDF listo — se analizará al continuar</small
            >
          </div>
          <button
            class="btn-quitar-pdf"
            @click.stop="emit('update:pdfFile', null)"
            title="Quitar PDF"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </template>
    </div>

    <div class="row g-3">
      <!-- N° Pedido -->
      <div class="col-12 col-md-4">
        <label class="form-label label-divisan small"
          >N° Pedido (Folio) <span class="text-danger">*</span></label
        >
        <input
          v-model="form.numero_pedido"
          type="number"
          class="form-control divisan-input-modal"
          placeholder="Ingreso manual"
        />
      </div>

      <!-- Cliente / Contacto de venta al público -->
      <div class="col-12 col-md-8" v-click-outside="cerrarCliente">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <label class="form-label label-divisan small mb-0">
            {{ form.venta_publico ? "Contacto (venta al público)" : "Cliente" }}
            <span class="text-danger">*</span>
          </label>
          <button
            type="button"
            class="switch-venta-publico"
            :class="{ activo: form.venta_publico }"
            @click="form.venta_publico = !form.venta_publico"
          >
            <span class="switch-track"><span class="switch-thumb"></span></span>
            <span class="switch-txt">Venta al público</span>
          </button>
        </div>

        <template v-if="!form.venta_publico">
          <div class="custom-select-wrapper">
            <input
              v-model="clienteBusqueda"
              type="text"
              class="form-control divisan-input-modal custom-select-input"
              :placeholder="form.cliente ? nombreSeleccionado : 'Buscar cliente...'"
              :class="{ 'tiene-valor': form.cliente && !clienteBusqueda }"
              @focus="abierto = true"
              @input="abierto = true"
            />
            <!-- Flecha: hace toggle del dropdown -->
            <i
              class="bi custom-select-arrow"
              :class="abierto ? 'bi-chevron-up' : 'bi-chevron-down'"
              @mousedown.prevent="toggleCliente"
            ></i>
            <div v-if="abierto" class="custom-dropdown">
              <div v-if="clientesFiltrados.length === 0" class="custom-dropdown-empty">
                Sin resultados
              </div>
              <div
                v-for="c in clientesFiltrados"
                :key="c.id"
                class="custom-dropdown-item"
                :class="{ selected: form.cliente === c.id }"
                @mousedown.prevent="elegirCliente(c)"
              >
                <span class="item-codigo">{{ c.numero_cliente }}</span>
                <span class="item-nombre">{{ c.nombre }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- Se guarda en el propio pedido, no en el catálogo: así dos ventas de
             mostrador en proceso al mismo tiempo no se pisan el contacto. -->
        <div v-else class="row g-2">
          <div class="col-12">
            <input
              v-model="form.contacto_nombre"
              type="text"
              class="form-control divisan-input-modal"
              placeholder="Nombre del cliente"
            />
          </div>
          <div class="col-12" v-click-outside="cerrarEstado">
            <div class="custom-select-wrapper">
              <input
                v-model="estadoBusqueda"
                type="text"
                class="form-control divisan-input-modal custom-select-input"
                :placeholder="form.contacto_estado ? estadoLabelSeleccionado : 'Selecciona un estado...'"
                :class="{ 'tiene-valor': form.contacto_estado && !estadoBusqueda }"
                @focus="estadoAbierto = true"
                @input="estadoAbierto = true"
              />
              <i
                class="bi custom-select-arrow"
                :class="estadoAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                @mousedown.prevent="estadoAbierto = !estadoAbierto"
              ></i>
              <div v-if="estadoAbierto" class="custom-dropdown">
                <div v-if="estadosFiltrados.length === 0" class="custom-dropdown-empty">
                  Sin resultados
                </div>
                <div
                  v-for="e in estadosFiltrados"
                  :key="e.value"
                  class="custom-dropdown-item"
                  :class="{ selected: form.contacto_estado === e.value }"
                  @mousedown.prevent="elegirEstado(e)"
                >
                  {{ e.label }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="telefono-lada">
              <select v-model="contactoPrefijo" class="lada-select">
                <option v-for="l in LADAS" :key="l.codigo" :value="l.codigo">
                  {{ l.bandera }} {{ l.codigo }}
                </option>
              </select>
              <input
                v-model="contactoTelefonoInput"
                type="text"
                inputmode="numeric"
                class="form-control divisan-input-modal tel-input"
                :placeholder="'8448935204'.slice(0, contactoMaxLongitud)"
              />
            </div>
            <small
              v-if="form.contacto_telefono && form.contacto_telefono.length !== contactoMaxLongitud"
              class="text-danger d-block mt-1"
            >
              <i class="bi bi-exclamation-triangle-fill me-1"></i>
              Debe tener {{ contactoMaxLongitud }} dígitos — tiene {{ form.contacto_telefono.length }}.
            </small>
            <small v-else class="text-cafe opacity-50 d-block mt-1">
              <i class="bi bi-whatsapp me-1 text-verde"></i>
              Solo dígitos, sin espacios ni guiones ({{ form.contacto_telefono.length }}/{{ contactoMaxLongitud }}).
            </small>
          </div>
        </div>
      </div>

      <!-- Fecha recibo -->
      <div class="col-12 col-md-4">
        <label class="form-label label-divisan small"
          >Fecha de recibo <span class="text-danger">*</span></label
        >
        <input
          v-model="form.fecha_recibo"
          type="date"
          class="form-control divisan-input-modal"
        />
      </div>

      <!-- Prioridad (dropdown custom con flecha toggle) -->
      <div class="col-12 col-md-4" v-click-outside="cerrarPrioridad">
        <label class="form-label label-divisan small">Prioridad</label>
        <div class="custom-select-wrapper">
          <div
            class="form-control divisan-input-modal custom-select-input prioridad-display"
            @click="abiertoPrioridad = !abiertoPrioridad"
          >
            <span v-if="form.prioridad === 'alta'">🔴 Alta</span>
            <span v-else-if="form.prioridad === 'media'">🟡 Media</span>
            <span v-else-if="form.prioridad === 'baja'">🟢 Baja</span>
            <span v-else class="text-muted">Seleccionar...</span>
          </div>
          <i
            class="bi custom-select-arrow"
            :class="abiertoPrioridad ? 'bi-chevron-up' : 'bi-chevron-down'"
            @mousedown.prevent="abiertoPrioridad = !abiertoPrioridad"
            style="pointer-events: auto; cursor: pointer"
          ></i>
          <div v-if="abiertoPrioridad" class="custom-dropdown">
            <div
              v-for="op in prioridades"
              :key="op.value"
              class="custom-dropdown-item"
              :class="{ selected: form.prioridad === op.value }"
              @mousedown.prevent="elegirPrioridad(op.value)"
            >
              {{ op.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- Notas -->
      <div class="col-12">
        <label class="form-label label-divisan small">Notas (opcional)</label>
        <textarea
          v-model="form.notas"
          class="form-control divisan-input-modal"
          rows="2"
          placeholder="Observaciones del pedido..."
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { ESTADOS_MEXICO } from "@/utils/estadosMexico";
import { LADAS, LADA_LONGITUD, soloDigitos } from "@/utils/ladas";

const props = defineProps({
  formPedido: { type: Object, required: true },
  pdfFile: { type: Object, default: null },
  catClientes: { type: Array, default: () => [] },
  fechaLimite: { type: String, default: "" },
});
const emit = defineEmits(["update:formPedido", "update:pdfFile"]);

// computed bidireccional — v-model sobre objeto del padre
const form = computed({
  get: () => props.formPedido,
  set: (v) => emit("update:formPedido", v),
});

// ── Contacto de venta al público (teléfono con lada) ────────
const contactoMaxLongitud = computed(() => LADA_LONGITUD[form.value.contacto_prefijo] || 10);

const contactoTelefonoInput = computed({
  get: () => form.value.contacto_telefono,
  set: (val) => {
    form.value.contacto_telefono = soloDigitos(val).slice(0, contactoMaxLongitud.value);
  },
});

// Al cambiar la lada, re-trunca el número ya escrito a la nueva longitud.
const contactoPrefijo = computed({
  get: () => form.value.contacto_prefijo,
  set: (val) => {
    form.value.contacto_prefijo = val;
    form.value.contacto_telefono = soloDigitos(form.value.contacto_telefono).slice(
      0,
      LADA_LONGITUD[val] || 10
    );
  },
});

// ── Dropdown de estado (búsqueda + máximo 5 renglones visibles) ──
const estadoBusqueda = ref("");
const estadoAbierto = ref(false);

const estadosFiltrados = computed(() => {
  const q = estadoBusqueda.value.trim().toLowerCase();
  if (!q) return ESTADOS_MEXICO;
  return ESTADOS_MEXICO.filter(
    (e) => e.label.toLowerCase().includes(q) || e.value.toLowerCase().includes(q)
  );
});

const estadoLabelSeleccionado = computed(
  () => ESTADOS_MEXICO.find((e) => e.value === form.value.contacto_estado)?.label || ""
);

function elegirEstado(e) {
  form.value.contacto_estado = e.value;
  estadoAbierto.value = false;
  estadoBusqueda.value = "";
}
function cerrarEstado() {
  estadoAbierto.value = false;
  estadoBusqueda.value = "";
}

// ── Dropdown cliente ──────────────────────────────────────
const clienteBusqueda = ref("");
const abierto = ref(false);

const clientesFiltrados = computed(() => {
  const q = clienteBusqueda.value.toLowerCase();
  return props.catClientes
    .filter(
      (c) =>
        !q ||
        c.numero_cliente.toLowerCase().includes(q) ||
        c.nombre.toLowerCase().includes(q)
    )
    .slice(0, 5);
});

const nombreSeleccionado = computed(() => {
  const c = props.catClientes.find((x) => x.id === props.formPedido.cliente);
  return c ? `${c.numero_cliente} — ${c.nombre}` : "";
});

function elegirCliente(c) {
  emit("update:formPedido", { ...props.formPedido, cliente: c.id });
  clienteBusqueda.value = "";
  abierto.value = false;
}
function cerrarCliente() {
  abierto.value = false;
  clienteBusqueda.value = "";
}
function toggleCliente() {
  abierto.value = !abierto.value;
}

// ── Dropdown prioridad ────────────────────────────────────
const abiertoPrioridad = ref(false);
const prioridades = [
  { value: "alta", label: "🔴 Alta" },
  { value: "media", label: "🟡 Media" },
  { value: "baja", label: "🟢 Baja" },
];

function elegirPrioridad(v) {
  emit("update:formPedido", { ...props.formPedido, prioridad: v });
  abiertoPrioridad.value = false;
}
function cerrarPrioridad() {
  abiertoPrioridad.value = false;
}

// ── PDF ───────────────────────────────────────────────────
function handleChange(e) {
  emit("update:pdfFile", e.target.files[0] || null);
}
function handleDrop(e) {
  const f = e.dataTransfer.files[0];
  if (f?.type === "application/pdf") emit("update:pdfFile", f);
}

// ── Directiva click-outside ───────────────────────────────
const vClickOutside = {
  mounted(el, b) {
    el._co = (e) => {
      if (!el.contains(e.target)) b.value(e);
    };
    document.addEventListener("click", el._co);
  },
  unmounted(el) {
    document.removeEventListener("click", el._co);
  },
};
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-verde-oscuro {
  color: var(--d-green-dark);
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}

.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
  color: var(--d-brown);
  background: white;
}
.divisan-input-modal:focus {
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 0.2rem rgba(40, 54, 24, 0.15);
  outline: none;
}
.tiene-valor {
  color: var(--d-green-dark) !important;
  font-weight: 700;
}

/* ── Switch venta al público ────────────────────────────── */
.switch-venta-publico {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.switch-track {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 18px;
  border-radius: 10px;
  background: rgba(96, 108, 56, 0.3);
  transition: background 0.15s;
}
.switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  transition: transform 0.15s;
}
.switch-venta-publico.activo .switch-track {
  background: var(--d-green-dark);
}
.switch-venta-publico.activo .switch-thumb {
  transform: translateX(16px);
}
.switch-txt {
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--d-green);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* ── Teléfono con select de lada (contacto de venta al público) ─ */
.telefono-lada {
  display: flex;
  align-items: stretch;
  border: 1px solid var(--d-green);
  border-radius: 8px;
  overflow: hidden;
  background: white;
}
.lada-select {
  appearance: none;
  -webkit-appearance: none;
  background: rgba(96, 108, 56, 0.07)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath fill='%23606c38' d='M0 0l4 5 4-5z'/%3E%3C/svg%3E")
    no-repeat right 7px center;
  border: none;
  border-right: 1px solid rgba(96, 108, 56, 0.25);
  padding: 0 22px 0 8px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
}
.lada-select:focus {
  outline: none;
}
.telefono-lada .tel-input {
  border: none !important;
  border-radius: 0 !important;
  flex: 1;
  box-shadow: none !important;
}

/* ── Upload zona compacta (horizontal) ───────────────────── */
.upload-zone {
  border: 2px dashed var(--d-green);
  border-radius: 12px;
  background: rgba(96, 108, 56, 0.05);
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  transition: 0.2s;
}
.upload-zone:hover,
.upload-zone.has-file {
  border-color: var(--d-green-dark);
  background: rgba(40, 54, 24, 0.07);
}
.upload-inner {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}
.upload-icon {
  font-size: 1.6rem;
  color: var(--d-green);
  flex-shrink: 0;
}
.upload-inner p,
.upload-inner small {
  line-height: 1.3;
}
.btn-quitar-pdf {
  margin-left: auto;
  background: none;
  border: none;
  color: #c0392b;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.15s;
}
.btn-quitar-pdf:hover {
  background: rgba(192, 57, 43, 0.1);
}

/* ── Custom select (cliente + prioridad) ─────────────────── */
.custom-select-wrapper {
  position: relative;
}
.custom-select-input {
  padding-right: 2.2rem !important;
}

.prioridad-display {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  min-height: 38px;
}

.custom-select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.8rem;
  cursor: pointer; /* clickeable para toggle */
}

.custom-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 1080;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(40, 54, 24, 0.15);
  max-height: 185px;
  overflow-y: auto;
}
.custom-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
}
.custom-dropdown-item:last-child {
  border-bottom: none;
}
.custom-dropdown-item:hover,
.custom-dropdown-item.selected {
  background: rgba(96, 108, 56, 0.12);
}
.custom-dropdown-empty {
  padding: 10px 12px;
  color: rgba(64, 34, 8, 0.4);
  font-size: 0.85rem;
  text-align: center;
}

.item-codigo {
  font-family: var(--d-font-mono);
  font-size: 0.8rem;
  font-weight: 700;
  background: var(--d-green-dark);
  color: var(--d-cream);
  border-radius: 4px;
  padding: 1px 6px;
  flex-shrink: 0;
}
.item-nombre {
  font-size: 0.85rem;
  color: var(--d-brown);
}
</style>
